import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelfServiceLanguage } from '../../context/SelfServiceLanguageContext';
import { deviceSalesService } from '../../services/posApi';
import { terminalIntegrationService } from '../../services/terminalIntegration';
import SelfServiceFooter from '../../components/SelfServiceFooter';
import vkQr from '@vkontakte/vk-qr';
import './SelfService.css';

const SelfServicePaymentScreen = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t, language } = useSelfServiceLanguage();

    const [cart] = useState(location.state?.cart || []);
    const [customer] = useState(location.state?.customer || null);
    const [usePoints] = useState(location.state?.usePoints || false);

    // Payment states: 'loading', 'waiting_payment', 'processing_sale', 'success', 'error'
    const [paymentState, setPaymentState] = useState('loading');
    const [errorMessage, setErrorMessage] = useState('');
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [receiptId, setReceiptId] = useState(null);
    const [countdown, setCountdown] = useState(15);
    const qrContainerRef = useRef(null);

    // Check if running in Electron
    const isElectron = Boolean(window?.process?.versions?.electron);

    useEffect(() => {
        if (cart.length === 0) {
            navigate('/device/self-service');
        }
    }, [cart, navigate]);

    const getTotalAmount = () => {
        return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    };

    const getFinalAmount = () => {
        const total = getTotalAmount();
        const pointsDiscount = getPointsDiscount();
        return total - pointsDiscount;
    };

    // Validate bonus amount (copied from device logic)
    const validateBonusAmount = (bonusAmount, cartItems, totalAmount) => {
        if (bonusAmount <= 0) return true;
        if (!Number.isInteger(bonusAmount)) return false;

        const originalTotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        const targetTotal = originalTotal - bonusAmount;

        let accumulatedTotal = 0;
        cartItems.forEach(item => {
            const itemTotal = item.price * item.quantity;
            const proportion = itemTotal / originalTotal;
            const adjustedTotal = Math.round(targetTotal * proportion * 100) / 100;
            const adjustedUnitPrice = Math.round((adjustedTotal / item.quantity) * 100) / 100;
            const itemTotalAmount = Math.round((adjustedUnitPrice * item.quantity) * 100) / 100;
            accumulatedTotal += itemTotalAmount;
        });

        const diff = Math.round((targetTotal - accumulatedTotal) * 100) / 100;
        if (Math.abs(diff) >= 0.01) {
            const firstItem = cartItems[0];
            const correctedUnitPrice = Math.round((firstItem.price - (bonusAmount * firstItem.price * firstItem.quantity / originalTotal) + diff / firstItem.quantity) * 100) / 100;
            const correctedTotal = Math.round((correctedUnitPrice * firstItem.quantity) * 100) / 100;
            accumulatedTotal = accumulatedTotal - Math.round((firstItem.price * firstItem.quantity) * 100) / 100 + correctedTotal;
        }

        const finalTotal = Math.round(accumulatedTotal * 100) / 100;
        const nonBonusTotal = Math.round((totalAmount - bonusAmount) * 100) / 100;

        return finalTotal === nonBonusTotal;
    };

    // Find maximum valid bonus amount (copied from device logic)
    const findMaxValidBonusAmount = (maxAmount, cartItems, totalAmount) => {
        let validAmount = Math.floor(maxAmount);

        while (validAmount > 0) {
            if (validateBonusAmount(validAmount, cartItems, totalAmount)) {
                return validAmount;
            }
            validAmount -= 1;
        }

        return 0;
    };

    const getPointsDiscount = () => {
        if (!usePoints || !customer) return 0;
        const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        // Maximum 99% of total, rounded down to integer
        const maxTheoretical = Math.floor(Math.min(customer.balance, total * 0.99));
        // Find maximum mathematically valid amount
        const maxPoints = findMaxValidBonusAmount(maxTheoretical, cart, total);
        console.log('Payment screen bonus:', { total, balance: customer.balance, maxTheoretical, maxPoints, usePoints });
        return maxPoints;
    };

    // Find Card payment method on mount
    useEffect(() => {
        const findPaymentMethod = async () => {
            try {
                const deviceInfo = JSON.parse(localStorage.getItem('device_register_info') || '{}');
                const methods = deviceInfo?.payment_methods || [];

                // Find first Card payment method
                const cardMethod = methods.find(m => m.type === 'card' || m.name.toLowerCase().includes('карт'));

                if (!cardMethod) {
                    throw new Error('Способ оплаты картой не найден');
                }

                setPaymentMethod(cardMethod);
                setPaymentState('waiting_payment');
            } catch (error) {
                console.error('Error finding payment method:', error);
                setErrorMessage(error.message || 'Ошибка поиска способа оплаты');
                setPaymentState('error');
            }
        };

        findPaymentMethod();
    }, []);

    // Auto-start terminal payment for Electron mode
    useEffect(() => {
        if (paymentState === 'waiting_payment' && isElectron && paymentMethod) {
            handleTerminalPayment();
        }
    }, [paymentState, isElectron, paymentMethod]);

    // Countdown timer for success screen
    useEffect(() => {
        if (paymentState === 'success' && countdown > 0) {
            const timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (paymentState === 'success' && countdown === 0) {
            // Reset and go to start - use replace to clear history
            navigate('/device/self-service', { replace: true, state: null });
        }
    }, [paymentState, countdown, navigate]);

    // Generate QR code when success state is reached
    useEffect(() => {
        if (paymentState === 'success' && receiptId && qrContainerRef.current) {
            const qrUrl = `https://receipt.minimarket116.kz/r/${receiptId}`;
            const qrSvg = vkQr.createQR(qrUrl, {
                qrSize: 296, // 370 * 0.8 = 296 (20% smaller)
                isShowLogo: false,
                foregroundColor: '#005A40'
            });
            qrContainerRef.current.innerHTML = qrSvg;
        }
    }, [paymentState, receiptId]);

    const handleGoBack = () => {
        navigate(-1);
    };

    const handlePaymentSuccess = async (terminalPaymentDetails = null) => {
        setPaymentState('processing_sale');

        try {
            const totalAmount = getTotalAmount();
            const pointsDiscount = getPointsDiscount();

            // Create sale data matching device format
            const saleData = {
                items: cart.map(item => ({
                    id: item.external_id || item.id,
                    total_amount: item.price * item.quantity,
                    unit_price: item.price,
                    name: item.name,
                    quantity: item.quantity,
                    ...(item.mark_code && { mark_code: item.mark_code })
                })),
                payments: (() => {
                    const payments = [];

                    // Add bonus payment if using points
                    if (usePoints && pointsDiscount > 0) {
                        payments.push({
                            type: 'bonus',
                            sum: pointsDiscount
                        });
                    }

                    // Add card payment for remaining amount
                    const cardAmount = totalAmount - pointsDiscount;
                    if (cardAmount > 0) {
                        const cardPayment = {
                            type: 'card',
                            sum: cardAmount,
                            payment_method_id: paymentMethod.id
                        };

                        // Add terminal payment details if available
                        if (terminalPaymentDetails) {
                            cardPayment.payment_details = terminalPaymentDetails;
                        }

                        payments.push(cardPayment);
                    }

                    return payments;
                })(),
                customer: {
                    tax_id: "",
                    id: customer?.id || null
                }
            };

            const response = await deviceSalesService.createSale(saleData);
            console.log('Sale creation response:', response);

            if (!response || response.result === 'error' || response.status === 'error') {
                throw new Error(response?.message || 'Ошибка при создании продажи');
            }

            // Get receipt ID from response
            const receiptIdFromResponse = response.receipt?.id || response.id;

            if (!receiptIdFromResponse) {
                console.error('No receipt ID in response:', response);
                throw new Error('Не удалось получить ID чека');
            }

            setReceiptId(receiptIdFromResponse);
            setPaymentState('success');
            setCountdown(15);
        } catch (error) {
            console.error('Sale creation error:', error);
            setErrorMessage(error.message || 'Ошибка создания чека');
            setPaymentState('error');
        }
    };

    const handleTerminalPayment = async () => {
        if (!isElectron) return;

        try {
            const deviceInfo = JSON.parse(localStorage.getItem('device_register_info') || '{}');
            const posType = deviceInfo.pos_type || 'kaspi';

            const totalAmount = getTotalAmount();
            const pointsDiscount = getPointsDiscount();
            const finalAmount = getFinalAmount();

            console.log('Terminal payment:', {
                totalAmount,
                pointsDiscount,
                finalAmount,
                usePoints,
                customer: customer?.balance
            });

            const paymentResult = await terminalIntegrationService.processPayment(
                {
                    posType,
                    amount: finalAmount
                },
                (status) => {
                    console.log('Payment status:', status);
                }
            );

            if (paymentResult.success) {
                await handlePaymentSuccess(paymentResult.paymentDetails);
            } else {
                throw new Error(paymentResult.error || 'Ошибка оплаты');
            }
        } catch (error) {
            console.error('Terminal payment error:', error);
            setErrorMessage(error.message || 'Ошибка терминала');
            setPaymentState('error');
        }
    };

    // Security badge SVG по языку
    const getSecurityBadge = () => {
        if (language === 'ru') {
            return (
                <svg width="318" height="115" viewBox="0 0 318 115" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M66.1335 33V9.90001H84.8235V33H81.1135V13.225H69.8435V33H66.1335ZM108.109 24.39C108.109 26.9567 107.304 29.1033 105.694 30.83C104.107 32.5333 102.1 33.385 99.6736 33.385C97.2936 33.385 95.3803 32.5567 93.9336 30.9V39.3H90.2586V15.815H93.6886V18.23C94.9953 16.3633 96.9903 15.43 99.6736 15.43C102.1 15.43 104.107 16.2933 105.694 18.02C107.304 19.7233 108.109 21.8467 108.109 24.39ZM102.824 28.45C103.827 27.4 104.329 26.0583 104.329 24.425C104.329 22.7917 103.827 21.45 102.824 20.4C101.82 19.3267 100.572 18.79 99.0786 18.79C97.632 18.79 96.407 19.28 95.4036 20.26C94.4003 21.24 93.8986 22.6167 93.8986 24.39C93.8986 26.1867 94.4003 27.575 95.4036 28.555C96.407 29.535 97.632 30.025 99.0786 30.025C100.572 30.025 101.82 29.5 102.824 28.45ZM126.129 15.43H127.389V33H123.679V22.325L113.669 33.385H112.374V15.815H116.084V26.56L126.129 15.43ZM134.179 15.815H146.814V33H143.139V19H137.609L137.574 20.715C137.457 25.195 136.967 28.4033 136.104 30.34C135.264 32.2533 133.829 33.21 131.799 33.21C131.309 33.21 130.866 33.14 130.469 33V29.92C130.842 29.9667 131.076 29.99 131.169 29.99C132.196 29.99 132.931 29.22 133.374 27.68C133.817 26.14 134.062 23.62 134.109 20.12L134.179 15.815ZM151.073 24.39C151.026 21.94 151.901 19.8283 153.698 18.055C155.518 16.2583 157.665 15.3833 160.138 15.43C162.635 15.3833 164.793 16.2583 166.613 18.055C168.433 19.8517 169.32 21.9633 169.273 24.39C169.32 26.84 168.433 28.9633 166.613 30.76C164.793 32.5567 162.635 33.4317 160.138 33.385C157.665 33.4317 155.518 32.5567 153.698 30.76C151.901 28.94 151.026 26.8167 151.073 24.39ZM165.493 24.425C165.493 22.8383 164.98 21.5083 163.953 20.435C162.926 19.3383 161.666 18.79 160.173 18.79C158.656 18.79 157.385 19.3383 156.358 20.435C155.331 21.5083 154.818 22.8383 154.818 24.425C154.818 26.0117 155.32 27.3417 156.323 28.415C157.35 29.465 158.621 29.99 160.138 29.99C161.655 29.99 162.926 29.4533 163.953 28.38C164.98 27.3067 165.493 25.9883 165.493 24.425ZM171.328 15.815H175.773L182.353 23.795L175.108 33H170.803L178.013 23.795L171.328 15.815ZM182.493 33V15.815H186.168V33H182.493ZM190.683 23.69L197.998 33H193.518L186.273 23.795L192.888 15.815H197.298L190.683 23.69ZM214.962 15.43H216.222V33H212.512V22.325L202.502 33.385H201.207V15.815H204.917V26.56L214.962 15.43ZM225.042 33V19H219.512V15.815H234.247V19H228.717V33H225.042ZM252.941 25.615H240.131C240.364 27.015 240.982 28.1233 241.986 28.94C243.012 29.7333 244.261 30.13 245.731 30.13C247.551 30.13 249.219 29.4533 250.736 28.1L252.486 30.62C250.549 32.4633 248.262 33.385 245.626 33.385C243.012 33.385 240.819 32.5333 239.046 30.83C237.272 29.1033 236.386 26.9683 236.386 24.425C236.339 21.9517 237.202 19.8283 238.976 18.055C240.749 16.2583 242.849 15.3833 245.276 15.43C247.539 15.43 249.429 16.1883 250.946 17.705C252.462 19.1983 253.221 21.065 253.221 23.305C253.221 23.9817 253.127 24.7517 252.941 25.615ZM240.201 22.745H249.546C249.522 21.4617 249.091 20.4467 248.251 19.7C247.411 18.93 246.372 18.545 245.136 18.545C243.922 18.545 242.861 18.93 241.951 19.7C241.064 20.4467 240.481 21.4617 240.201 22.745ZM114.39 77V59.815H118.1V77H114.39ZM122.58 67.655L130.14 77H125.66L118.24 67.795L125.135 59.815H129.475L122.58 67.655ZM146.77 66.605V77H143.34V75.075C142.057 76.615 140.225 77.385 137.845 77.385C136.048 77.385 134.602 76.93 133.505 76.02C132.408 75.11 131.86 73.9083 131.86 72.415C131.86 70.9217 132.432 69.7317 133.575 68.845C134.718 67.9583 136.258 67.515 138.195 67.515H143.06V66.395C143.06 65.205 142.733 64.295 142.08 63.665C141.45 63.035 140.552 62.72 139.385 62.72C137.635 62.72 136.048 63.5717 134.625 65.275L132.49 63.105C134.17 60.655 136.585 59.43 139.735 59.43C141.882 59.43 143.585 60.06 144.845 61.32C146.128 62.58 146.77 64.3417 146.77 66.605ZM143.06 70.49V70.175H138.65C136.62 70.175 135.605 70.875 135.605 72.275C135.605 72.975 135.873 73.535 136.41 73.955C136.97 74.3517 137.705 74.55 138.615 74.55C139.875 74.55 140.925 74.165 141.765 73.395C142.628 72.625 143.06 71.6567 143.06 70.49ZM169.735 68.39C169.735 70.9567 168.93 73.1033 167.32 74.83C165.733 76.5333 163.726 77.385 161.3 77.385C158.92 77.385 157.006 76.5567 155.56 74.9V83.3H151.885V59.815H155.315V62.23C156.621 60.3633 158.616 59.43 161.3 59.43C163.726 59.43 165.733 60.2933 167.32 62.02C168.93 63.7233 169.735 65.8467 169.735 68.39ZM164.45 72.45C165.453 71.4 165.955 70.0583 165.955 68.425C165.955 66.7917 165.453 65.45 164.45 64.4C163.446 63.3267 162.198 62.79 160.705 62.79C159.258 62.79 158.033 63.28 157.03 64.26C156.026 65.24 155.525 66.6167 155.525 68.39C155.525 70.1867 156.026 71.575 157.03 72.555C158.033 73.535 159.258 74.025 160.705 74.025C162.198 74.025 163.446 73.5 164.45 72.45ZM177.122 77V63H171.592V59.815H186.327V63H180.797V77H177.122ZM201.726 59.815H205.646L197.281 78.96C196.557 80.5933 195.694 81.7833 194.691 82.53C193.687 83.3 192.451 83.685 190.981 83.685C190.001 83.685 189.056 83.5683 188.146 83.335V80.22C189.102 80.3133 189.779 80.36 190.176 80.36C191.949 80.36 193.197 79.52 193.921 77.84L194.621 76.23L187.761 59.815H191.716L196.651 72.31L201.726 59.815Z" fill="#028A62"/>
                    <path d="M2 19V49.3726C2 62.9647 11.1382 74.8585 24.2715 78.36L154.197 113L293.298 78.1156C306.642 74.7689 316 62.7744 316 49.0167V19" stroke="#028A62" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            );
        } else if (language === 'kz') {
            return (
                <svg width="318" height="115" viewBox="0 0 318 115" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M90.384 33V9.90001H94.094V33H90.384ZM98.854 20.715L109.494 33H104.909L94.199 20.715L103.894 9.90001H108.444L98.854 20.715ZM126.074 22.605V33H122.644V31.075C121.361 32.615 119.529 33.385 117.149 33.385C115.352 33.385 113.906 32.93 112.809 32.02C111.712 31.11 111.164 29.9083 111.164 28.415C111.164 26.9217 111.736 25.7317 112.879 24.845C114.022 23.9583 115.562 23.515 117.499 23.515H122.364V22.395C122.364 21.205 122.037 20.295 121.384 19.665C120.754 19.035 119.856 18.72 118.689 18.72C116.939 18.72 115.352 19.5717 113.929 21.275L111.794 19.105C113.474 16.655 115.889 15.43 119.039 15.43C121.186 15.43 122.889 16.06 124.149 17.32C125.432 18.58 126.074 20.3417 126.074 22.605ZM122.364 26.49V26.175H117.954C115.924 26.175 114.909 26.875 114.909 28.275C114.909 28.975 115.177 29.535 115.714 29.955C116.274 30.3517 117.009 30.55 117.919 30.55C119.179 30.55 120.229 30.165 121.069 29.395C121.932 28.625 122.364 27.6567 122.364 26.49ZM149.039 24.39C149.039 26.9567 148.234 29.1033 146.624 30.83C145.037 32.5333 143.03 33.385 140.604 33.385C138.224 33.385 136.31 32.5567 134.864 30.9V39.3H131.189V15.815H134.619V18.23C135.925 16.3633 137.92 15.43 140.604 15.43C143.03 15.43 145.037 16.2933 146.624 18.02C148.234 19.7233 149.039 21.8467 149.039 24.39ZM143.754 28.45C144.757 27.4 145.259 26.0583 145.259 24.425C145.259 22.7917 144.757 21.45 143.754 20.4C142.75 19.3267 141.502 18.79 140.009 18.79C138.562 18.79 137.337 19.28 136.334 20.26C135.33 21.24 134.829 22.6167 134.829 24.39C134.829 26.1867 135.33 27.575 136.334 28.555C137.337 29.535 138.562 30.025 140.009 30.025C141.502 30.025 142.75 29.5 143.754 28.45ZM156.426 33V19H150.896V15.815H165.631V19H160.101V33H156.426ZM182.539 22.605V33H179.109V31.075C177.826 32.615 175.994 33.385 173.614 33.385C171.817 33.385 170.371 32.93 169.274 32.02C168.177 31.11 167.629 29.9083 167.629 28.415C167.629 26.9217 168.201 25.7317 169.344 24.845C170.487 23.9583 172.027 23.515 173.964 23.515H178.829V22.395C178.829 21.205 178.502 20.295 177.849 19.665C177.219 19.035 176.321 18.72 175.154 18.72C173.404 18.72 171.817 19.5717 170.394 21.275L168.259 19.105C169.939 16.655 172.354 15.43 175.504 15.43C177.651 15.43 179.354 16.06 180.614 17.32C181.897 18.58 182.539 20.3417 182.539 22.605ZM178.829 26.49V26.175H174.419C172.389 26.175 171.374 26.875 171.374 28.275C171.374 28.975 171.642 29.535 172.179 29.955C172.739 30.3517 173.474 30.55 174.384 30.55C175.644 30.55 176.694 30.165 177.534 29.395C178.397 28.625 178.829 27.6567 178.829 26.49ZM187.689 33V15.815H191.399V22.395H198.784V15.815H202.459V33H198.784V25.58H191.399V33H187.689ZM207.855 33V15.815H211.565V21.275H215.17C217.2 21.275 218.833 21.8 220.07 22.85C221.306 23.9 221.925 25.3233 221.925 27.12C221.925 28.9167 221.306 30.3517 220.07 31.425C218.833 32.475 217.2 33 215.17 33H207.855ZM224.13 33V15.815H227.805V33H224.13ZM211.565 29.815H215.03C216.033 29.815 216.803 29.5933 217.34 29.15C217.9 28.6833 218.18 28.0183 218.18 27.155C218.18 26.2917 217.9 25.6267 217.34 25.16C216.78 24.6933 215.998 24.46 214.995 24.46H211.565V29.815ZM97.3809 77V63H91.8509V59.815H106.586V63H101.056V77H97.3809ZM123.634 59.43H124.894V77H121.184V66.325L111.174 77.385H109.879V59.815H113.589V70.56L123.634 59.43ZM130.284 77V59.815H142.394V63H133.994V77H130.284ZM146.151 55.125C145.661 54.6583 145.416 54.0867 145.416 53.41C145.416 52.7333 145.661 52.1617 146.151 51.695C146.641 51.205 147.236 50.96 147.936 50.96C148.612 50.96 149.196 51.205 149.686 51.695C150.176 52.1617 150.421 52.7333 150.421 53.41C150.421 54.0867 150.176 54.6583 149.686 55.125C149.196 55.5917 148.612 55.825 147.936 55.825C147.236 55.825 146.641 55.5917 146.151 55.125ZM146.081 77V59.815H149.756V77H146.081ZM167.831 71.995C167.831 73.6983 167.224 75.0283 166.011 75.985C164.821 76.9183 163.176 77.385 161.076 77.385C158.043 77.385 155.709 76.3 154.076 74.13L156.316 71.925C157.529 73.395 159.058 74.13 160.901 74.13C161.928 74.13 162.733 73.92 163.316 73.5C163.899 73.0567 164.191 72.4733 164.191 71.75C164.191 71.05 163.911 70.5017 163.351 70.105C162.814 69.7083 162.056 69.51 161.076 69.51H159.081V66.57H160.971C161.764 66.57 162.371 66.395 162.791 66.045C163.211 65.695 163.421 65.2167 163.421 64.61C163.421 64.0267 163.188 63.56 162.721 63.21C162.278 62.86 161.648 62.685 160.831 62.685C159.174 62.685 157.774 63.3267 156.631 64.61L154.391 62.405C155.954 60.4217 158.159 59.43 161.006 59.43C162.896 59.43 164.366 59.8617 165.416 60.725C166.489 61.565 167.026 62.755 167.026 64.295C167.026 65.8817 166.408 67.095 165.171 67.935C166.944 68.7983 167.831 70.1517 167.831 71.995ZM172.469 55.125C171.979 54.6583 171.734 54.0867 171.734 53.41C171.734 52.7333 171.979 52.1617 172.469 51.695C172.959 51.205 173.554 50.96 174.254 50.96C174.931 50.96 175.514 51.205 176.004 51.695C176.494 52.1617 176.739 52.7333 176.739 53.41C176.739 54.0867 176.494 54.6583 176.004 55.125C175.514 55.5917 174.931 55.825 174.254 55.825C173.554 55.825 172.959 55.5917 172.469 55.125ZM172.399 77V59.815H176.074V77H172.399ZM195.654 81.935V77H192.819V69.58H185.434V77H181.724V59.815H185.434V66.395H192.819V59.815H196.494V73.815H199.224V81.935H195.654ZM203.436 55.125C202.946 54.6583 202.701 54.0867 202.701 53.41C202.701 52.7333 202.946 52.1617 203.436 51.695C203.926 51.205 204.521 50.96 205.221 50.96C205.897 50.96 206.481 51.205 206.971 51.695C207.461 52.1617 207.706 52.7333 207.706 53.41C207.706 54.0867 207.461 54.6583 206.971 55.125C206.481 55.5917 205.897 55.825 205.221 55.825C204.521 55.825 203.926 55.5917 203.436 55.125ZM203.366 77V59.815H207.041V77H203.366ZM225.116 71.995C225.116 73.6983 224.509 75.0283 223.296 75.985C222.106 76.9183 220.461 77.385 218.361 77.385C215.328 77.385 212.994 76.3 211.361 74.13L213.601 71.925C214.814 73.395 216.343 74.13 218.186 74.13C219.213 74.13 220.018 73.92 220.601 73.5C221.184 73.0567 221.476 72.4733 221.476 71.75C221.476 71.05 221.196 70.5017 220.636 70.105C220.099 69.7083 219.341 69.51 218.361 69.51H216.366V66.57H218.256C219.049 66.57 219.656 66.395 220.076 66.045C220.496 65.695 220.706 65.2167 220.706 64.61C220.706 64.0267 220.473 63.56 220.006 63.21C219.563 62.86 218.933 62.685 218.116 62.685C216.459 62.685 215.059 63.3267 213.916 64.61L211.676 62.405C213.239 60.4217 215.444 59.43 218.291 59.43C220.181 59.43 221.651 59.8617 222.701 60.725C223.774 61.565 224.311 62.755 224.311 64.295C224.311 65.8817 223.693 67.095 222.456 67.935C224.229 68.7983 225.116 70.1517 225.116 71.995Z" fill="#028A62"/>
                    <path d="M2 19V49.3726C2 62.9647 11.1382 74.8585 24.2715 78.36L154.197 113L293.298 78.1156C306.642 74.7689 316 62.7744 316 49.0167V19" stroke="#028A62" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            );
        } else {
            return (
                <svg width="318" height="106" viewBox="0 0 318 106" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M96.4584 33V13.225H88.6884V9.90001H107.973V13.225H100.168V33H96.4584ZM122.588 22.605V33H119.158V31.075C117.874 32.615 116.043 33.385 113.663 33.385C111.866 33.385 110.419 32.93 109.323 32.02C108.226 31.11 107.678 29.9083 107.678 28.415C107.678 26.9217 108.249 25.7317 109.393 24.845C110.536 23.9583 112.076 23.515 114.013 23.515H118.878V22.395C118.878 21.205 118.551 20.295 117.898 19.665C117.268 19.035 116.369 18.72 115.203 18.72C113.453 18.72 111.866 19.5717 110.443 21.275L108.308 19.105C109.988 16.655 112.403 15.43 115.553 15.43C117.699 15.43 119.403 16.06 120.663 17.32C121.946 18.58 122.588 20.3417 122.588 22.605ZM118.878 26.49V26.175H114.468C112.438 26.175 111.423 26.875 111.423 28.275C111.423 28.975 111.691 29.535 112.228 29.955C112.788 30.3517 113.523 30.55 114.433 30.55C115.693 30.55 116.743 30.165 117.583 29.395C118.446 28.625 118.878 27.6567 118.878 26.49ZM145.552 24.39C145.552 26.9567 144.747 29.1033 143.137 30.83C141.551 32.5333 139.544 33.385 137.117 33.385C134.737 33.385 132.824 32.5567 131.377 30.9V39.3H127.702V15.815H131.132V18.23C132.439 16.3633 134.434 15.43 137.117 15.43C139.544 15.43 141.551 16.2933 143.137 18.02C144.747 19.7233 145.552 21.8467 145.552 24.39ZM140.267 28.45C141.271 27.4 141.772 26.0583 141.772 24.425C141.772 22.7917 141.271 21.45 140.267 20.4C139.264 19.3267 138.016 18.79 136.522 18.79C135.076 18.79 133.851 19.28 132.847 20.26C131.844 21.24 131.342 22.6167 131.342 24.39C131.342 26.1867 131.844 27.575 132.847 28.555C133.851 29.535 135.076 30.025 136.522 30.025C138.016 30.025 139.264 29.5 140.267 28.45ZM158.507 24.39C158.46 21.94 159.347 19.8283 161.167 18.055C162.987 16.2583 165.134 15.3833 167.607 15.43C170.827 15.43 173.242 16.76 174.852 19.42L172.262 21.45C171.049 19.7 169.509 18.825 167.642 18.825C166.102 18.825 164.819 19.3617 163.792 20.435C162.765 21.5083 162.252 22.8267 162.252 24.39C162.252 25.9767 162.765 27.3067 163.792 28.38C164.819 29.4533 166.102 29.99 167.642 29.99C169.462 29.99 171.002 29.1267 172.262 27.4L174.852 29.395C174.082 30.655 173.067 31.635 171.807 32.335C170.547 33.035 169.147 33.385 167.607 33.385C165.064 33.385 162.905 32.51 161.132 30.76C159.382 29.01 158.507 26.8867 158.507 24.39ZM192.554 22.605V33H189.124V31.075C187.84 32.615 186.009 33.385 183.629 33.385C181.832 33.385 180.385 32.93 179.289 32.02C178.192 31.11 177.644 29.9083 177.644 28.415C177.644 26.9217 178.215 25.7317 179.359 24.845C180.502 23.9583 182.042 23.515 183.979 23.515H188.844V22.395C188.844 21.205 188.517 20.295 187.864 19.665C187.234 19.035 186.335 18.72 185.169 18.72C183.419 18.72 181.832 19.5717 180.409 21.275L178.274 19.105C179.954 16.655 182.369 15.43 185.519 15.43C187.665 15.43 189.369 16.06 190.629 17.32C191.912 18.58 192.554 20.3417 192.554 22.605ZM188.844 26.49V26.175H184.434C182.404 26.175 181.389 26.875 181.389 28.275C181.389 28.975 181.657 29.535 182.194 29.955C182.754 30.3517 183.489 30.55 184.399 30.55C185.659 30.55 186.709 30.165 187.549 29.395C188.412 28.625 188.844 27.6567 188.844 26.49ZM207.503 15.815V19.07H206.383C204.89 19.07 203.677 19.49 202.743 20.33C201.81 21.17 201.343 22.3367 201.343 23.83V33H197.668V15.815H201.098V18.055C202.382 16.445 204.05 15.64 206.103 15.64C206.57 15.64 207.037 15.6983 207.503 15.815ZM223.507 6.75001H227.217V33H223.787V30.55C222.457 32.44 220.45 33.385 217.767 33.385C215.363 33.385 213.357 32.5333 211.747 30.83C210.16 29.1033 209.367 26.9683 209.367 24.425C209.32 21.905 210.113 19.77 211.747 18.02C213.38 16.2467 215.387 15.3833 217.767 15.43C220.24 15.43 222.153 16.2583 223.507 17.915V6.75001ZM222.072 28.555C223.075 27.575 223.577 26.1983 223.577 24.425C223.577 22.6517 223.063 21.275 222.037 20.295C221.033 19.2917 219.82 18.79 218.397 18.79C216.903 18.79 215.643 19.315 214.617 20.365C213.613 21.415 213.112 22.7567 213.112 24.39C213.112 26.0233 213.613 27.3767 214.617 28.45C215.62 29.5 216.88 30.025 218.397 30.025C219.843 30.025 221.068 29.535 222.072 28.555Z" fill="#028A62"/>
                    <path d="M2 10V40.3726C2 53.9647 11.1382 65.8585 24.2715 69.36L154.197 104L293.298 69.1156C306.642 65.7689 316 53.7744 316 40.0167V10" stroke="#028A62" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            );
        }
    };

    return (
        <div className="self-service-container" style={{ background: 'white' }}>
            {/* Content */}
            <div className="self-service-content">
                {(paymentState === 'loading' || paymentState === 'processing_sale') && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        width: '100%'
                    }}>
                        <div style={{
                            width: 150,
                            height: 150,
                            border: '12px solid rgba(2, 138, 98, 0.2)',
                            borderTop: '12px solid #028A62',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }}/>
                    </div>
                )}

                {paymentState === 'waiting_payment' && (
                    <div style={{ textAlign: 'center', maxWidth: 900, margin: '0 auto' }}>
                        {/* Terminal image */}
                        <div style={{ marginBottom: 48 }}>
                            <img
                                src="/pos-illustration.png"
                                alt="Terminal"
                                style={{ width: '100%', maxWidth: 400, height: 'auto' }}
                            />
                        </div>

                        {/* Payment text */}
                        <div style={{
                            fontSize: 38,
                            fontWeight: 500,
                            fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                            color: '#028A62',
                            marginBottom: 64,
                            lineHeight: 1.5,
                            whiteSpace: 'pre-line'
                        }}>
                            {t('applyCardOrScanQR')}
                        </div>

                        {/* Manual payment buttons for non-Electron mode */}
                        {!isElectron && (
                            <div style={{ display: 'flex', gap: 24, justifyContent: 'center', maxWidth: 800, margin: '0 auto' }}>
                                <button
                                    onClick={handlePaymentSuccess}
                                    style={{
                                        flex: 1,
                                        height: 124,
                                        background: '#028A62',
                                        border: 'none',
                                        borderRadius: 50,
                                        fontSize: 38,
                                        fontWeight: 700,
                                        fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                                        color: 'white',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {t('paymentSuccess')}
                                </button>
                                <button
                                    onClick={() => {
                                        setErrorMessage('Оплата не прошла');
                                        setPaymentState('error');
                                    }}
                                    style={{
                                        flex: 1,
                                        height: 124,
                                        background: '#DC3545',
                                        border: 'none',
                                        borderRadius: 50,
                                        fontSize: 38,
                                        fontWeight: 700,
                                        fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                                        color: 'white',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {t('paymentFailed')}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {paymentState === 'success' && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        height: '100%',
                        maxWidth: 900,
                        margin: '0 auto',
                        paddingBottom: '175px' // Space for footer
                    }}>
                        <div style={{ textAlign: 'center', width: '100%' }}>
                            {/* Success title - same size as waiting screen title */}
                            <h1 style={{
                                fontSize: 115,
                                fontWeight: 700,
                                fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                                color: '#17181A',
                                marginBottom: 64,
                                marginTop: 80,
                                lineHeight: 1.1,
                                whiteSpace: 'pre-line'
                            }}>
                                {t('thankYouForPurchase')}
                            </h1>

                            {/* Fiscal receipt text - smaller */}
                            <div style={{
                                fontSize: 38,
                                fontWeight: 500,
                                fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                                color: '#17181A',
                                marginBottom: 32
                            }}>
                                {t('fiscalReceipt')}
                            </div>

                            {/* QR Code */}
                            <div style={{
                                display: 'inline-block',
                                background: 'white',
                                borderRadius: 50,
                                padding: 40,
                                marginBottom: 48
                            }}>
                                <div ref={qrContainerRef} style={{ width: 296, height: 296 }} />
                            </div>
                        </div>

                        {/* Bottom section with countdown and button */}
                        <div style={{ textAlign: 'center', width: '100%' }}>
                            {/* Countdown text */}
                            <div style={{
                                fontSize: 38,
                                fontWeight: 500,
                                fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                                color: '#17181A',
                                marginBottom: 32
                            }}>
                                {t('startNewPurchase')} {countdown} {t('seconds')}
                            </div>

                            {/* Start new purchase button - full width */}
                            <button
                                onClick={() => navigate('/device/self-service', { replace: true, state: null })}
                                style={{
                                    width: '100%',
                                    height: 124,
                                    background: '#028A62',
                                    border: 'none',
                                    borderRadius: 50,
                                    fontSize: 38,
                                    fontWeight: 700,
                                    fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                                    color: 'white',
                                    cursor: 'pointer'
                                }}
                            >
                                {t('startNewPurchaseButton')}
                            </button>
                        </div>
                    </div>
                )}

                {paymentState === 'error' && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        height: '100%',
                        maxWidth: 800,
                        margin: '0 auto',
                        paddingTop: '20%'
                    }}>
                        {/* Error message - centered vertically */}
                        <div style={{
                            fontSize: 38,
                            fontWeight: 500,
                            fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                            color: '#17181A',
                            lineHeight: 1.4,
                            textAlign: 'center'
                        }}>
                            {errorMessage}
                        </div>

                        {/* Button at bottom */}
                        <button
                            onClick={handleGoBack}
                            style={{
                                width: '100%',
                                maxWidth: 600,
                                height: 124,
                                background: '#028A62',
                                border: 'none',
                                borderRadius: 50,
                                fontSize: 38,
                                fontWeight: 700,
                                fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                                color: 'white',
                                cursor: 'pointer',
                                marginBottom: 32
                            }}
                        >
                            {t('goBack')}
                        </button>
                    </div>
                )}
            </div>

            {/* Security badge - only on payment screen */}
            {paymentState === 'waiting_payment' && (
                <div style={{
                    position: 'fixed',
                    bottom: 130,
                    right: 65,
                    zIndex: 10
                }}>
                    {getSecurityBadge()}
                </div>
            )}

            {/* Footer */}
            <SelfServiceFooter />

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default SelfServicePaymentScreen;
